import {ApolloError} from '@apollo/client';
import {get} from 'lodash';

import {type WeaveApp} from './weave';

export class UseNodeValueServerExecutionError extends Error {}

export class HTTPError<T = unknown> extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public body?: T
  ) {
    super(message);
  }
}

export function parseHTTPErrorBody(
  body: unknown
): Record<string, unknown> | null {
  if (typeof body !== 'string' || body.length === 0) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

// Pull a human-readable string out of an HTTPError body. Prefer recognized
// structured messages while preserving the existing raw-body fallback.
export function parseHTTPErrorDetail(body: unknown): string | null {
  const obj = parseHTTPErrorBody(body);
  if (obj) {
    for (const key of ['detail', 'reason', 'message']) {
      const val = obj[key];
      if (typeof val === 'string' && val.length > 0) {
        return val;
      }
    }

    if (Array.isArray(obj.detail)) {
      for (const error of obj.detail) {
        if (
          error !== null &&
          typeof error === 'object' &&
          'msg' in error &&
          typeof error.msg === 'string' &&
          error.msg.length > 0
        ) {
          return error.msg;
        }
      }
    }
  }

  return typeof body === 'string' && body.length > 0 ? body : null;
}

// Read the object digests the server reports as missing on a
// RefObjectsNotFoundError
export function getMissingObjectDigests(e: unknown): Set<string> {
  if (!(e instanceof HTTPError)) {
    return new Set();
  }
  const {missing_object_digests: digests} = parseHTTPErrorBody(e.body) ?? {};
  return new Set(Array.isArray(digests) ? digests : []);
}

export function extractErrorMessageFromApolloError(
  err: unknown
): string | undefined {
  if (!(err instanceof ApolloError) && !(err instanceof Error)) {
    return;
  }
  const errMsg =
    get(err, 'graphQLErrors[0].message') ??
    get(err, 'networkError.result.errors[0].message') ??
    get(err, 'message');
  return errMsg;
}

export function extractStatusCodeFromApolloError(err: any): number | undefined {
  const statusCode =
    err?.networkError?.statusCode ?? err?.graphQLErrors?.[0]?.statusCode;
  return statusCode;
}

type DDErrorPayload = {
  errorMessage?: string;
  errorName?: string;
  errorStack?: string;
  hashedErrorMessage?: number;
  hashedErrorStack?: number;
  windowLocationURL?: string;
  weaveContext?: any;
  isServerError?: boolean;
};

export const weaveErrorToDDPayload = (
  error: Error,
  weave?: WeaveApp,
  uuid?: string
): DDErrorPayload => {
  try {
    return {
      errorMessage: trimString(error.message),
      errorName: trimString(error.name),
      errorStack: trimString(error.stack),
      hashedErrorMessage: hashStr(error.message),
      hashedErrorStack: hashStr(error.stack),
      windowLocationURL: trimString(window.location.href),
      weaveContext: weave?.client.debugMeta(),
      isServerError: error instanceof UseNodeValueServerExecutionError,
      ...(uuid != null && {uuid}),
    };
  } catch (e) {
    // If we fail to serialize the error, just return an empty object.
    console.error('Failed to serialize error', e);
    return {};
  }
};

const MAX_CONFIG_STRING_LENGTH = 2500;
export const trimString = (str?: string) => {
  if (str == null) {
    return '';
  }
  if (str.length > MAX_CONFIG_STRING_LENGTH) {
    return str.slice(0, MAX_CONFIG_STRING_LENGTH);
  }
  return str;
};

export const hashStr = (str?: string) => {
  // From https://stackoverflow.com/a/7616484
  let hash = 0;
  let i: number;
  let chr: number;
  if (str == null || str.length === 0) {
    return hash;
  }
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};
